<script setup lang="ts">
import { Subscribe } from '~/entities/subscribe';
import Pagination from '~/shared/ui/pagination/Pagination.vue';
import {
  CLIENT_API_SECTOR_DETAIL_KEY,
  CLIENT_API_SECTOR_DETAIL_URL,
  CLIENT_API_SECTOR_MATERIALS_KEY,
} from '~/shared/api/client/news/news.const';
import type {
  ClientApiNewsPaginatedResponse,
  INewsListingItemDataClientDTO,
} from '~/shared/api/client/news/news.dto';
import type { SectorDTO } from '~/shared/api/client/tags/sector.dto';
import { CLIENT_NEWS_ON_PAGE } from '~/pages/news';
import { SEO_TAG_DESCRIPTION, SEO_TAG_TITLE } from '~/shared/lib/seo';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { getMaterialLink } from '~/shared/lib/getMaterialLink';
import { useOrigin } from '~/shared/lib/useOrigin';
import { declineWords } from '~/shared/utils/declineWords';

const route = useRoute();
const router = useRouter();

const sectorId = computed(() => Number(route.params.id));

if (!sectorId.value) {
  throw createError({
    status: 404,
    message: 'Тег не найден',
    fatal: true,
  });
}

const { data, error } = await useCustomFetch<SectorDTO>(
  () => `${CLIENT_API_SECTOR_DETAIL_URL}${sectorId.value}/`,
  {
    method: 'GET',
    key: CLIENT_API_SECTOR_DETAIL_KEY,
  },
);

if (error.value || !data.value) {
  throw createError({
    status: 404,
    message: 'Тег не найден',
    fatal: true,
  });
}

const sector = computed(() => data.value);

const title = computed(() => sector.value?.name || '');

const heading = computed(() => `Материалы с тегом «${title.value}»`);

const pageFromQuery = computed(() => {
  const page = Number(route.query.page);
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
});

const pageNumber = useState(
  'client-sector-materials-page',
  () => pageFromQuery.value,
);

if (pageNumber.value !== pageFromQuery.value) {
  pageNumber.value = pageFromQuery.value;
}

const { data: materials, status: materialsStatus } = await useCustomFetch<
  ClientApiNewsPaginatedResponse<INewsListingItemDataClientDTO>
>(() => `${CLIENT_API_SECTOR_DETAIL_URL}${sectorId.value}/materials/`, {
  method: 'GET',
  key: CLIENT_API_SECTOR_MATERIALS_KEY,
  watch: [pageNumber],
  params: {
    page: pageNumber,
    page_size: CLIENT_NEWS_ON_PAGE,
  },
});

const materialsCount = computed(() => materials.value?.count ?? 0);

if (sector.value) {
  const origin = useOrigin();
  const url = origin + route.path;

  const metaTitle = SEO_TAG_TITLE.replace('%%TAG%%', heading.value);
  const metaDescription = SEO_TAG_DESCRIPTION.replace('%%TAG%%', heading.value);

  useHead({
    title: metaTitle,
    link: [{ rel: 'canonical', href: url }],
    meta: [
      { name: 'description', content: metaDescription },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: url },
      { property: 'og:title', content: metaTitle },
      { property: 'og:description', content: metaDescription },
    ],
  });
}

watch(
  () => pageFromQuery.value,
  (value) => {
    if (pageNumber.value !== value) pageNumber.value = value;
  },
);

watch(
  () => pageNumber.value,
  (value) => {
    if (!import.meta.client) return;

    const nextQuery = { ...route.query };

    if (value <= 1) {
      delete nextQuery.page;
    } else {
      nextQuery.page = String(value);
    }

    router.push({ path: route.path, query: nextQuery });

    nextTick(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  },
);
</script>

<template>
  <div :class="$style.tag__page">
    <div :class="$style.tag__header">
      <div :class="$style.tag__header__top">
        <ButtonBack />
        <div v-if="materialsCount" :class="$style.tag__header__results">
          {{ materialsCount }}
          {{
            declineWords(materialsCount, [
              'Результат',
              'Результата',
              'Результатов',
            ])
          }}
        </div>
      </div>

      <h1 :class="$style.tag__title">{{ heading }}</h1>
    </div>

    <div :class="$style.tag__wrapper">
      <Loader v-if="materialsStatus === 'pending'" />

      <NewsItemCard
        v-for="(item, index) in materials?.results"
        v-else-if="materials?.results && materials.results.length > 0"
        :key="`sector-material-${index}`"
        :title="item.title"
        :category="item.category?.name || false"
        :category-link="item.category ? `/category/${item.category.id}` : false"
        :date="item.publication_date"
        :subject="item.subject"
        :link="getMaterialLink(item)"
        :image="item.image?.photo.file_url"
      />

      <div
        v-else-if="materials?.results && materials.results.length === 0"
        :class="$style.noMaterials"
      >
        <div :class="$style.noMaterials__title">Материалов по тегу нет</div>
      </div>

      <Pagination
        v-if="materialsStatus === 'success'"
        theme="client"
        page-name="client-sector-materials-page"
        :total="materials?.count || 0"
        :sibling-count="1"
        show-edges
        :items-per-page="CLIENT_NEWS_ON_PAGE"
        :default-page="pageNumber"
        @page-change="pageNumber = $event"
      />
      <div v-else-if="materialsStatus === 'error'" :class="$style.noMaterials">
        <div :class="$style.noMaterials__title">
          Не удалось получить материалы
        </div>
      </div>
    </div>

    <Subscribe :class="$style.subscribe" />
  </div>
</template>

<style module lang="scss">
.tag__page {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
}

.tag__header {
  padding: var(--space-2-5x);

  &__top {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--space-2-5x);
  }

  &__results {
    @include text_caption1_caps;
    color: var(--color-gray-text);
    text-transform: uppercase;
  }
}

.tag__title {
  font-weight: 600;
  font-size: 40px;
  line-height: 110%;
  color: var(--color-black-1);
  margin: 0;
}

.tag__wrapper {
  height: 100%;
  flex: 1;
  border-bottom: 1px solid var(--color-gray-line);
}

.noMaterials {
  padding: var(--space-2-5x);

  &__title {
    @include text_h5;
  }
}

@media (max-width: 479px) {
  .tag__header {
    padding: 16px;
  }

  .tag__title {
    @include text_h4;
  }

  .noMaterials {
    padding: var(--space-2x);
  }
}
</style>
